import type { Transition, Variants } from "motion/react";

export const introEaseOut: Transition = {
  duration: 0.45,
  ease: [0.25, 0.1, 0.25, 1],
};

export const introFadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

export const introFadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

export const introStaggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.08 },
  },
};

export const introScaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1 },
};

/** Intro 2 radar — Figma PC 96344:109821 cohort `durationMs: 2000` (H5 同构节奏). */
export const INTRO2_CYCLE_S = 2;

/** 光束全程不停转：~4s/圈 linear（bug + 视觉扫光叙事；与 scene 2s 循环解耦）. */
export const RADAR_SWEEP_DURATION_S = 4;

/** Figma Radar scanner 初始角（conic / beam 对齐）. */
export const INTRO2_SWEEP_BASE_DEG = 92;

/**
 * Ed center — Figma 96344:109855
 * t=0→0.25: opacity 0→1 + scale 0→1 (spring), then hold.
 */
export const INTRO2_ED_CENTER_OPACITY = {
  keyframes: [0, 1, 1] as number[],
  times: [0, 0.25, 1] as number[],
};
export const INTRO2_ED_CENTER_SCALE = {
  keyframes: [0, 1, 1] as number[],
  times: [0, 0.25, 1] as number[],
};

/**
 * Ed glow — Figma 96344:109836
 * t=0→0.25 fade in; mid hold; mild breath after reveal.
 */
export const INTRO2_ED_GLOW_OPACITY = {
  keyframes: [0, 1, 1, 0.9, 1] as number[],
  times: [0, 0.25, 0.6, 0.85, 1] as number[],
};
export const INTRO2_ED_GLOW_SCALE = {
  keyframes: [1, 1, 1.06, 1.08, 1] as number[],
  times: [0, 0.6, 0.85, 0.95, 1] as number[],
};

/**
 * Orbit rings — Figma 96344:109837 / 109838
 * outer: appear 0.16→0.36; inner: 0.10→0.30 (scale 0.7→1).
 */
export const INTRO2_ORBIT_OUTER = {
  opacity: [0, 0, 1, 1] as number[],
  scale: [0.7, 0.7, 1, 1] as number[],
  times: [0, 0.16, 0.36, 1] as number[],
};
export const INTRO2_ORBIT_INNER = {
  opacity: [0, 0, 1, 1] as number[],
  scale: [0.7, 0.7, 1, 1] as number[],
  times: [0, 0.1, 0.3, 1] as number[],
};

export type Intro2TagKey = "cash" | "investing" | "wealth" | "decisions";

/**
 * Domain tags — Figma 96344:109839/842/851/848
 * 按光束扫过方位依次点亮（非固定 stagger 一齐亮）:
 * Cash → Investing → Wealth → Decisions.
 * 单 tag: opacity + 轻微位移 + scale 0.85→1, ~250ms ease-out.
 */
export const INTRO2_TAG_MOTION: Record<
  Intro2TagKey,
  {
    from: { x: number; y: number };
    opacityTimes: [number, number, number, number];
  }
> = {
  cash: { from: { x: -3, y: -8 }, opacityTimes: [0, 0.275, 0.425, 1] },
  investing: { from: { x: 8, y: -2 }, opacityTimes: [0, 0.325, 0.475, 1] },
  wealth: { from: { x: -7, y: 4 }, opacityTimes: [0, 0.375, 0.525, 1] },
  decisions: { from: { x: 3, y: 8 }, opacityTimes: [0, 0.425, 0.575, 1] },
};

export const INTRO2_TAG_SCALE_IN = [0.85, 0.85, 1, 1] as const;

export const TYPING_SEQUENCE_MS = {
  dotsVisible: 1200,
  bubbleVisible: 400,
} as const;

/**
 * Intro 3 goal cards — Figma 96293:45207 / 45219 / 45231
 * delayChildren 360ms + stagger 120ms; enter 420ms (y + opacity).
 */
export const INTRO3_CARD_DELAY_CHILDREN_S = 0.36;
export const INTRO3_CARD_STAGGER_S = 0.12;
export const INTRO3_CARD_ENTER_DURATION_S = 0.42;
/** Figma y ease [0.45, 1.45, 0.8, 1] */
export const INTRO3_CARD_ENTER_Y_EASE = [0.45, 1.45, 0.8, 1] as const;

export const intro3CardStaggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: INTRO3_CARD_STAGGER_S,
      delayChildren: INTRO3_CARD_DELAY_CHILDREN_S,
    },
  },
};

export const intro3GoalCardEnter: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      opacity: {
        duration: INTRO3_CARD_ENTER_DURATION_S,
        ease: "easeOut",
      },
      y: {
        duration: INTRO3_CARD_ENTER_DURATION_S,
        ease: INTRO3_CARD_ENTER_Y_EASE,
      },
    },
  },
};

/** Progress fill · Safety 660→1320ms (~0.66s), staggered +120ms per card. */
export const INTRO3_BAR_FILL_DURATION_S = 0.66;
export const INTRO3_BAR_FILL_BASE_DELAY_S = 0.66;
export const INTRO3_BAR_STAGGER_S = 0.12;

/** Bar shimmer starts after fill (Safety ~2040ms), then loops ~1.8s + 0.54s hold. */
export const INTRO3_SHIMMER_BASE_DELAY_S = 2.04;
export const INTRO3_SHIMMER_STAGGER_S = 0.3;
export const INTRO3_SHIMMER_SWEEP_DURATION_S = 1.8;
export const INTRO3_SHIMMER_REPEAT_DELAY_S = 0.54;
export const INTRO3_SHIMMER_FADE_DURATION_S = 0.18;

export function intro3BarFillDelayS(cardIndex: number): number {
  return INTRO3_BAR_FILL_BASE_DELAY_S + cardIndex * INTRO3_BAR_STAGGER_S;
}

export function intro3ShimmerDelayS(cardIndex: number): number {
  return INTRO3_SHIMMER_BASE_DELAY_S + cardIndex * INTRO3_SHIMMER_STAGGER_S;
}
